var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useState } from 'react';
import { tokens } from '../tokens';
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect';
function mediaQueryStringFromBreakpoint(breakpoint) {
    return "screen and (min-width: ".concat(breakpoint, ")");
}
var breakpointsToResultsMap = {
    isAboveSmall: mediaQueryStringFromBreakpoint(tokens.breakpoints.small),
    isAboveMedium: mediaQueryStringFromBreakpoint(tokens.breakpoints.medium),
    isAboveLarge: mediaQueryStringFromBreakpoint(tokens.breakpoints.large),
};
function initialMatchResults() {
    var keys = Object.keys(breakpointsToResultsMap);
    return keys.reduce(function (results, resultKey) {
        var _a;
        return __assign(__assign({}, results), (_a = {}, _a[resultKey] = false, _a));
    }, {});
}
/**
 * For each breakpoint from design tokens (small, medium, large), determine if there is a media query match between it and a current viewport.
 *
 * Following mobile-first approach, when using the hook, consider base return without conditional rendering to be the mobile one.
 *
 * @returns Object of boolean values with `isAboveSmall`, `isAboveMedium`, and `isAboveLarge` entries
 */
function useMediaQuery() {
    var _a = __read(useState(initialMatchResults()), 2), matchResults = _a[0], setMatchResults = _a[1];
    useIsomorphicLayoutEffect(function () {
        function handleMediaChange() {
            var results = Object.keys(breakpointsToResultsMap).reduce(function (results, resultKey) {
                var _a;
                return __assign(__assign({}, results), (_a = {}, _a[resultKey] = window.matchMedia(breakpointsToResultsMap[resultKey]).matches, _a));
            }, {});
            setMatchResults(results);
        }
        handleMediaChange();
        // Add event listeners for each breakpoint
        // Safari doesn't support addEventListener for matchMedia, so using addListener as a fallback
        Object.values(breakpointsToResultsMap).forEach(function (queryString) {
            var media = window.matchMedia(queryString);
            try {
                media.addEventListener('change', handleMediaChange);
            }
            catch (err) {
                media.addListener(handleMediaChange);
            }
        });
        return function () {
            Object.values(breakpointsToResultsMap).forEach(function (queryString) {
                var media = window.matchMedia(queryString);
                try {
                    media.removeEventListener('change', handleMediaChange);
                }
                catch (err) {
                    media.removeListener(handleMediaChange);
                }
            });
        };
    }, []);
    return matchResults;
}
export default useMediaQuery;
